import { doc, getDoc, Timestamp } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
    fetchEvent,
    fetchEventFighters,
    liveStreamTicketsCollection,
} from "../utils/firebase";
import { Clock, Crown, FileText, MapPin, Radio, Skull, Ticket, X, CircleHelp, ScrollText } from "lucide-react";
import Markdown from "react-markdown";
import {
    formatFirestoreTimestamp,
    formatFirestoreTimestampToTime,
    getEventFighterWeightClass,
    getContrastTextColor,
    toTimestamp,
} from "../utils/common";
import { twMerge } from "tailwind-merge";
import EventFighterCard from "../components/event-fighter-card";
import FighterMatchCard from "../components/fighter-match-card";
import {
    FIGHTER_CONSENT_AND_RESPONSIBILITY_AGREEMENT_PATH,
    LANDING_PATH,
    LIVE_STREAM_PATH,
    PURCHASE_TICKET_PATH,
    REGISTER_EVENT_PATH,
    RULES_PATH,
} from "../App";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { GiPunch } from "react-icons/gi";
import { weightClassesName } from "../constants";
import { useAuth } from "../contexts/auth-context";

export default function Event() {
    const { t } = useTranslation();
    const { id } = useParams();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const liveStreamTicketCode = searchParams.get("liveStreamTicketCode");
    const [event, setEvent] = useState(null);
    const [timeLeft, setTimeLeft] = useState(0);
    const [eventFighters, setEventFighters] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(!!liveStreamTicketCode);
    const [code, setCode] = useState(liveStreamTicketCode ?? "");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const isAdmin = currentUser?.role?.toUpperCase() === "ADMIN";

    const handleBack = () => {
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            navigate(LANDING_PATH, { replace: true });
        }
    };

    const handleRegisterFight = async () => {
        navigate(`${REGISTER_EVENT_PATH}/${id}`);
    };

    useEffect(() => {
        if (timeLeft <= 0) return; // Stop the timer if it reaches 0

        const timer = setInterval(() => {
            setTimeLeft((prevTime) => prevTime - 1);
        }, 1000);

        // Clean up the interval on component unmount or when timeLeft reaches 0
        return () => clearInterval(timer);
    }, [timeLeft]);

    useEffect(() => {
        (async () => {
            if (location?.state?.id === id) {
                console.log("using state", location?.state);
                setEvent({
                    ...location?.state,
                    dateTime: Timestamp.fromMillis(
                        location?.state?.dateTime?.seconds * 1000
                    ),
                    registerExpiredDateTime: Timestamp.fromMillis(
                        location?.state?.registerExpiredDateTime?.seconds * 1000
                    ),
                    liveStreamTicketPurchaseDeadline: toTimestamp(
                        location?.state?.liveStreamTicketPurchaseDeadline
                    ),
                });
                location.state = null;
            } else {
                const fetchedEvent = await fetchEvent({
                    id: id,
                });

                if (!!fetchedEvent.data) {
                    console.log("using fetchedEvent", fetchedEvent.data);
                    setEvent({
                        ...fetchedEvent.data,
                        dateTime: !fetchedEvent.data.dateTime?._seconds
                            ? null
                            : Timestamp.fromMillis(
                                fetchedEvent.data.dateTime?._seconds * 1000
                            ),
                        registerExpiredDateTime: !fetchedEvent.data
                            .registerExpiredDateTime?._seconds
                            ? null
                            : Timestamp.fromMillis(
                                fetchedEvent.data.registerExpiredDateTime
                                    ?._seconds * 1000
                            ),
                        createdAt: !fetchedEvent.data.createdAt?._seconds
                            ? null
                            : Timestamp.fromMillis(
                                fetchedEvent.data.createdAt?._seconds * 1000
                            ),
                        updateAt: !fetchedEvent.data.updateAt?._seconds
                            ? null
                            : Timestamp.fromMillis(
                                fetchedEvent.data.updateAt?._seconds * 1000
                            ),
                        liveStreamTicketPurchaseDeadline: toTimestamp(
                            fetchedEvent.data.liveStreamTicketPurchaseDeadline
                        ),
                    });
                } else {
                    navigate("/404");
                }
            }
        })();
    }, [id, navigate]);

    useEffect(() => {
        (async () => {
            if (event?.id) {
                const fetchedEventFighters = await fetchEventFighters({
                    eventId: event?.id,
                });
                setEventFighters(fetchedEventFighters?.data ?? []);
            }
        })();
    }, [event]);

    // Open modal and prefill code if ?code=123456 is in the URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlCode = params.get("code");
        if (urlCode && /^\d{6}$/.test(urlCode)) {
            setCode(urlCode);
            setIsModalOpen(true);
        }
    }, [location.search]);

    // Open modal when ?openLiveStream=true is present (no code prefill)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const open = params.get("openLiveStream");
        if (open === "true") {
            setIsModalOpen(true);
            setCode("");
            setError("");
        }
    }, [location.search]);

    // Open modal when Live Stream button is clicked
    const handleOpenModal = (e) => {
        console.log("handleOpenModal", event);
        if (event?.isTest) return toast(t("event.testNotice"));
        const liveStreamTicketPurchaseDeadline =
            !!event.liveStreamTicketPurchaseDeadline
                ? Timestamp.fromMillis(
                    event.liveStreamTicketPurchaseDeadline?.seconds * 1000
                )?.toDate()
                : null;
        if (
            !event.liveStreamTicketPrice ||
            event.liveStreamTicketPrice <= 0 ||
            !liveStreamTicketPurchaseDeadline
        ) {
            return toast(t("common.comingSoon"));
        }

        // Note: Do not block modal after sales end; users with codes still need access

        e.preventDefault();
        setIsModalOpen(true);
        setCode("");
        setError("");
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCode("");
        setError("");
    };

    // Input change handler: only allow 6 digits
    const handleCodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, "").slice(0, 6);
        setCode(value);
        if (error) setError("");
    };

    // Validation and submit handler (to be implemented later)
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!code || code.length !== 6) {
            setError(t("event.liveStreamModal.error.required"));
            return;
        }

        try {
            const liveStreamTicketSnapshot = await getDoc(
                doc(liveStreamTicketsCollection, `${id}_${code}`)
            );
            if (liveStreamTicketSnapshot.exists()) {
                navigate(`${LIVE_STREAM_PATH}/${id}?code=${code}`);
            } else {
                toast.error(t("event.liveStreamModal.error.invalid"));
            }
        } catch (err) {
            console.error("Error validating code:", err);
            toast.error(t("event.liveStreamModal.error.invalid"));
        }
    };

    const handleBuyTicket = (e) => {
        if (event?.isTest) return toast(t("event.testNotice"));
        const liveStreamTicketPurchaseDeadline =
            !!event?.liveStreamTicketPurchaseDeadline
                ? Timestamp.fromMillis(
                    event.liveStreamTicketPurchaseDeadline?.seconds * 1000
                )?.toDate()
                : null;

        if (
            !event?.liveStreamTicketPrice ||
            event.liveStreamTicketPrice <= 0 ||
            !liveStreamTicketPurchaseDeadline
        ) {
            return toast(t("common.comingSoon"));
        }

        if (liveStreamTicketPurchaseDeadline < new Date()) {
            return toast(t("event.liveStreamModal.error.salesEnded"));
        }

        if (event.ticketUrl) {
            window.open(event.ticketUrl, "_blank");
        } else {
            toast(t("common.comingSoon"));
        }
    };

    // Live stream ticket sales are closed once the event has ended or the
    // purchase deadline has passed. Deadline may be `.seconds` (events list)
    // or `._seconds` (direct fetch); handle both. The server still enforces
    // this authoritatively — this only hides the purchase CTA.
    const liveStreamSaleClosed = (() => {
        if (!event) return false;
        if (event.isEnded === true) return true;
        const deadlineSeconds =
            event.liveStreamTicketPurchaseDeadline?.seconds ??
            event.liveStreamTicketPurchaseDeadline?._seconds ??
            null;
        if (!deadlineSeconds) return true;
        return deadlineSeconds * 1000 < Date.now();
    })();

    return (
        <div className="page-size flex flex-row gap-4 items-start p-4 relative max-[980px]:flex-col">
            {!event ? (
                <div className="flex-center page-size">
                    <div className="loading loading-bars loading-lg" />
                </div>
            ) : (
                <>
                    <div className="flex-1 w-1/2 h-[calc(100vh-9rem)] fixed top-20 left-4 max-[980px]:relative max-[980px]:w-[calc(100%-1rem)] max-[980px]:h-auto max-[980px]:min-h-[60vh] max-[980px]:top-auto max-[980px]:left-2 max-[980px]:mt-12 border-2">
                        <img
                            className="w-full h-full object-cover object-center abs-center opacity-50 blur-sm"
                            src={event.coverImage}
                            alt="Event Cover"
                        />
                        <img
                            className="w-full h-full object-contain object-center p-4 aspect-[3/4] abs-center shadow-lg"
                            src={event.coverImage}
                            alt="Event Cover"
                        />
                    </div>
                    <div className="flex-1 w-1/2 block mx-8 max-[980px]:hidden" />
                    <div className="flex-1 w-1/2 max-[980px]:w-full flex flex-col gap-4 h-full">
                        {event?.isTest && (
                            <div className="p-3 border-2 border-warning text-warning font-bold uppercase tracking-widest text-sm text-center">
                                {t("event.testBanner")}
                            </div>
                        )}
                        <div className="flex w-full">
                            <p className="text-[5em] self-start text-start font-bold border-l-[.8rem] pl-4 ml-2 leading-[.82] truncate w-full max-[980px]:text-[3rem]">
                                {t("event.title")}
                            </p>
                            <button
                                className="btn btn-ghost btn-md btn-square max-[980px]:fixed max-[980px]:top-2 max-[980px]:right-2 max-[980px]:btn-primary"
                                onClick={handleBack}
                            >
                                <X />
                            </button>
                        </div>
                        <p className="text-lg font-bold">{event.name}</p>
                        <div className="w-full prose">
                            <Markdown>{event?.details}</Markdown>
                        </div>

                        <br />
                        <p className="text-[5em] self-start text-start font-bold border-l-[.8rem] pl-4 ml-2 leading-[.82] truncate max-[980px]:text-[3rem]">
                            {t("event.joinNow")}
                        </p>
                        <div className="flex flex-row items-center justify-between w-full">
                            <div className="flex flex-col justify-between items-start w-full">
                                <p className="flex flex-row items-center gap-2">
                                    <Clock size={18} />
                                    {!!event?.dateTime
                                        ? formatFirestoreTimestamp(
                                            event.dateTime
                                        )
                                        : t("event.unknown")}{" "}
                                    |{" "}
                                    {!!event?.dateTime
                                        ? formatFirestoreTimestampToTime(
                                            event.dateTime
                                        )
                                        : t("event.unknown")}
                                </p>
                                <p className="">
                                    <MapPin
                                        className="inline mr-2 mb-[.25rem]"
                                        size={18}
                                    />
                                    <span className="w-2" />
                                    <span className="text-nowrap inline font-bold">
                                        {event?.venue?.trim() ||
                                            t("event.unknown")}
                                    </span>
                                    {!!event?.address?.trim() && (
                                        <>
                                            ,{" "}
                                            <span className="w-full">
                                                {event.address.trim()}
                                            </span>
                                        </>
                                    )}
                                </p>
                            </div>
                            <div className="flex flex-row gap-2 max-[980px]:flex-col">
                                <div className="flex flex-row justify-end items-center">
                                    <a
                                        target="_blank"
                                        href={event?.googleMap}
                                        className="btn btn-ghost btn-square btn-sm p-1"
                                        rel="noreferrer"
                                    >
                                        <img
                                            className="w-full h-full"
                                            src="/assets/img/logo-googlemaps.svg"
                                            alt="waze"
                                        />
                                    </a>
                                </div>
                                <div className="flex flex-row justify-end items-center">
                                    <a
                                        target="_blank"
                                        href={event?.waze}
                                        className="btn btn-ghost btn-square btn-sm p-1"
                                        rel="noreferrer"
                                    >
                                        <img
                                            className="w-full h-full"
                                            src="/assets/img/logo-waze.svg"
                                            alt="waze"
                                        />
                                    </a>
                                </div>
                            </div>
                        </div>
                        {event?.isEnded ? (
                            // Show event results when event is ended
                            <div className="w-full">
                                <div className="flex flex-row gap-4 p-4 border items-center bg-red-800">
                                    <Skull />
                                    <h3 className="font-bold">Event Ended</h3>
                                </div>
                                <br />

                                {!eventFighters ? (
                                    // Loading state for event results
                                    <div className="flex flex-col items-center gap-4 py-8">
                                        <div className="loading loading-bars loading-lg"></div>
                                        <p className="text-lg font-semibold">
                                            Loading Event Results...
                                        </p>
                                    </div>
                                ) : event?.eventResults &&
                                    event.eventResults.length > 0 ? (
                                    <div className="flex flex-col gap-4">
                                        <h3 className="text-xl font-bold">
                                            Event Results
                                        </h3>
                                        {event.eventResults.map(
                                            (result, index) => {
                                                const fighterA =
                                                    eventFighters?.find(
                                                        (ef) =>
                                                            ef.fighterId ===
                                                            result.fighterAId
                                                    );
                                                const fighterB =
                                                    eventFighters?.find(
                                                        (ef) =>
                                                            ef.fighterId ===
                                                            result.fighterBId
                                                    );

                                                if (!fighterA || !fighterB)
                                                    return null;

                                                return (
                                                    <FighterMatchCard
                                                        key={index}
                                                        fighterA={fighterA}
                                                        fighterB={fighterB}
                                                        winnerId={result.winnerId}
                                                    >
                                                        <div className="flex flex-col gap-2 w-full">
                                                            {result.isDraw ? (
                                                                <div className="flex border min-h-12 items-center">
                                                                    <div className="divider m-0 w-full px-4">
                                                                        DRAW
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="flex border min-h-12 items-center">
                                                                    <p className="px-4">
                                                                        WINNER:{" "}
                                                                        {result.winnerId ===
                                                                            fighterA.fighterId
                                                                            ? fighterA
                                                                                ?.fighter
                                                                                ?.name ||
                                                                            "Unknown Fighter"
                                                                            : fighterB
                                                                                ?.fighter
                                                                                ?.name ||
                                                                            "Unknown Fighter"}
                                                                    </p>
                                                                    <div className="flex-1 w-full" />
                                                                    {result.winBy && (
                                                                        <div className="h-full px-2 bg-white text-black min-w-12 flex-center font-bold min-h-12">
                                                                            {result.winBy.toUpperCase()}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </FighterMatchCard>
                                                );
                                            }
                                        )}
                                    </div>
                                ) : (
                                    <div className="alert alert-warning">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="stroke-current shrink-0 h-6 w-6"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                            />
                                        </svg>
                                        <span>
                                            No results available for this event.
                                        </span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Show normal buttons when event is not ended
                            <div className="w-full grid grid-cols-1 grid-rows-3  gap-2 md:grid-cols-2 md:grid-rows-2">
                                <button
                                    target="_blank"
                                    className="flex-1 btn btn-primary"
                                    rel="noreferrer"
                                    onClick={handleBuyTicket}
                                >
                                    <Ticket />
                                    {t("event.buttons.watch")}
                                </button>
                                <button
                                    type="button"
                                    className="flex-1 btn btn-primary"
                                    onClick={handleOpenModal}
                                >
                                    <Radio />
                                    {t("event.buttons.liveStream")}
                                </button>
                                {(() => {
                                    const registrationClosed =
                                        event?.isEnded ||
                                        event?.isTest ||
                                        (!!event?.registerExpiredDateTime?.toDate() &&
                                            event.registerExpiredDateTime.toDate() < new Date());
                                    return (
                                        <button
                                            className={twMerge(
                                                "flex-1 btn btn-primary w-full md:col-span-2"
                                            )}
                                            style={
                                                registrationClosed
                                                    ? undefined
                                                    : {
                                                        backgroundColor: `#${event?.color}`,
                                                        color: getContrastTextColor(event?.color),
                                                    }
                                            }
                                            onClick={handleRegisterFight}
                                            disabled={registrationClosed}
                                        >
                                            <GiPunch size={24} />
                                            {registrationClosed
                                                ? t("event.buttons.fightClosed")
                                                : t("event.buttons.fight")}
                                        </button>
                                    );
                                })()}
                                <div className="flex flex-wrap w-full md:col-span-2 gap-2">
                                    <a href="https://www.instagram.com/reel/DQdVp7QEV-T/?igsh=MTI4b3dseWtxeGlvdw%3D%3D" className="link inline-flex items-center gap-2 text-blue-400 font-bold hover:text-white hover:bg-blue-600 flex-1 md:w-auto justify-center px-4 py-2" target="_blank" rel="noreferrer">
                                        <CircleHelp className="inline" size={18} />
                                        <span className="whitespace-nowrap">{t("event.buttons.howToRegister")}</span>
                                    </a>
                                    <Link to={`${RULES_PATH}?eventId=${id}`} className="link inline-flex items-center gap-2 text-blue-400 font-bold hover:text-white hover:bg-blue-600 flex-1 md:w-auto justify-center px-4 py-2" target="_blank" rel="noreferrer">
                                        <ScrollText className="inline" size={18} />
                                        {t("event.buttons.fightRules")}
                                    </Link>
                                    <Link to={FIGHTER_CONSENT_AND_RESPONSIBILITY_AGREEMENT_PATH} className="link inline-flex items-center gap-2 text-blue-400 font-bold hover:text-white hover:bg-blue-600 flex-1 md:w-auto justify-center px-4 py-2" target="_blank" rel="noreferrer">
                                        <FileText className="inline" size={18} />
                                        <span className="whitespace-nowrap">{t("event.buttons.tnc")}</span>
                                    </Link>
                                </div>
                            </div>
                        )}
                        <div className="flex gap-2 w-full"></div>

                        {!event?.isEnded && (
                            <>
                                <br />
                                <p className="text-[5em] self-start text-start font-bold border-l-[.8rem] pl-4 ml-2 leading-[.82] truncate max-[980px]:text-[3rem]">
                                    {t("event.fighters")}
                                </p>

                                {!eventFighters ? (
                                    <div className="flex-center p-4">
                                        <div className="loading loading-bars loading-lg" />
                                    </div>
                                ) : eventFighters.filter((ef) => !ef.isPulledOut).length < 1 ? (
                                    <>
                                        <br />
                                        <p className="text-[5em] self-start text-start font-bold border-l-[.8rem] pl-4 ml-2 leading-[.82] truncate max-[980px]:text-[3rem]">
                                            {t("event.fighters")}
                                        </p>
                                        <div className="flex-center p-4 opacity-80">
                                            {t("event.noFighters")}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {(() => {
                                            const visibleFighters = eventFighters.filter(
                                                (ef) => !ef.isPulledOut
                                            );
                                            // Separate matched and unmatched fighters
                                            const matchedPairs = [];
                                            const matchedFighterIds = new Set();
                                            const unmatchedFighters = [];

                                            // Create matched pairs
                                            visibleFighters.forEach((fighter) => {
                                                if (fighter.matchWith && !matchedFighterIds.has(fighter.fighterId)) {
                                                    const matchedFighter = visibleFighters.find(
                                                        (f) => f.fighterId === fighter.matchWith
                                                    );
                                                    if (matchedFighter) {
                                                        matchedPairs.push({
                                                            fighterA: fighter,
                                                            fighterB: matchedFighter,
                                                        });
                                                        matchedFighterIds.add(fighter.fighterId);
                                                        matchedFighterIds.add(matchedFighter.fighterId);
                                                    }
                                                }
                                            });

                                            // Get unmatched fighters
                                            visibleFighters.forEach((fighter) => {
                                                if (!matchedFighterIds.has(fighter.fighterId)) {
                                                    unmatchedFighters.push(fighter);
                                                }
                                            });

                                            // Stable order by registration time (pinning was removed).
                                            const toMs = (ef) =>
                                                ef?.createdAt?.toMillis?.() ??
                                                (typeof ef?.createdAt?.seconds === "number"
                                                    ? ef.createdAt.seconds * 1000
                                                    : typeof ef?.createdAt?._seconds === "number"
                                                        ? ef.createdAt._seconds * 1000
                                                        : 0);
                                            const sortedUnmatchedFighters = unmatchedFighters.sort(
                                                (a, b) => toMs(a) - toMs(b)
                                            );

                                            return (
                                                <>
                                                    {/* Matched Fighters Section */}
                                                    {matchedPairs.length > 0 && (
                                                        <>
                                                            <p className="text-end font-bold">
                                                                {t("event.matchedFighters")}
                                                            </p>
                                                            <div className="flex flex-col gap-4 w-full">
                                                                {matchedPairs.map((pair, index) => (
                                                                    <FighterMatchCard
                                                                    className={index === 0 ? "" : "border-t pt-4"}
                                                                        key={index}
                                                                        fighterA={pair.fighterA}
                                                                        fighterB={pair.fighterB}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}

                                                    {/* Available Fighters Section */}
                                                    {sortedUnmatchedFighters.length > 0 && (
                                                        <>
                                                            <br />
                                                            <p className="text-end font-bold">
                                                                {t("event.availableFighters")}
                                                            </p>
                                                            <div className="flex flex-center gap-2">
                                                                {sortedUnmatchedFighters.map(
                                                                    (eventFighter, index) => {
                                                                        const fighter = eventFighter.fighter;
                                                                        const entry = eventFighter.entry;
                                                                        return (
                                                                            <React.Fragment key={index}>
                                                                                <EventFighterCard
                                                                                    fighter={fighter}
                                                                                    entry={entry}
                                                                                />
                                                                                {index !== sortedUnmatchedFighters.length - 1 ? (
                                                                                    <div className="divider m-0" />
                                                                                ) : null}
                                                                            </React.Fragment>
                                                                        );
                                                                    }
                                                                )}
                                                            </div>
                                                        </>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}
            {/* Simple, user-friendly DaisyUI Modal for Live Stream Access */}
            {isModalOpen && (
                <div className="modal modal-open" onClick={handleCloseModal}>
                    <div
                        className="modal-box relative border"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="btn btn-square btn-ghost absolute right-2 top-2"
                            onClick={handleCloseModal}
                            aria-label={t("event.liveStreamModal.close")}
                        >
                            <X />
                        </button>
                        <h3 className="font-bold text-lg mb-2">
                            {t("event.liveStreamModal.title")}
                        </h3>
                        <p className="mb-2 text-base text-gray-500 dark:text-gray-300">
                            {t("event.liveStreamModal.instructions")}
                        </p>
                        <br />
                        <div className="flex flex-col gap-4">
                            <label htmlFor="code" className="font-semibold">
                                {t("event.liveStreamModal.codeLabel")}
                            </label>
                            <input
                                id="code"
                                type="text"
                                inputMode="numeric"
                                pattern="\\d{6}"
                                maxLength={6}
                                className="input input-bordered text-center text-lg tracking-widest"
                                value={code}
                                onChange={handleCodeChange}
                                autoComplete="one-time-code"
                                placeholder={t(
                                    "event.liveStreamModal.codePlaceholder"
                                )}
                                disabled={isLoading}
                            />
                            {error && (
                                <div className="text-error text-sm">
                                    {t(error)}
                                </div>
                            )}
                            <div className="flex gap-2 mt-2 overflow-hidden">
                                <button
                                    type="submit"
                                    className={twMerge(
                                        "btn text-nowrap btn-primary transition-all duration-500",
                                        code?.length === 6 || liveStreamSaleClosed
                                            ? "w-full"
                                            : "w-1/2"
                                    )}
                                    disabled={isLoading || code.length !== 6}
                                    onClick={handleSubmit}
                                >
                                    {isLoading ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        t("event.liveStreamModal.enterBtn")
                                    )}
                                </button>
                                {!liveStreamSaleClosed && (
                                    <Link
                                        to={`${PURCHASE_TICKET_PATH}/${id}`}
                                        target="_blank"
                                        type="button"
                                        className={twMerge(
                                            "btn text-nowrap btn-outline transition-all duration-500 overflow-hidden",
                                            code?.length === 6
                                                ? "w-0 px-0"
                                                : "w-1/2 flex-1"
                                        )}
                                        disabled={isLoading}
                                    >
                                        {t("event.liveStreamModal.buyNowBtn")}
                                    </Link>
                                )}
                            </div>
                            {liveStreamSaleClosed && (
                                <p className="text-sm text-center opacity-70">
                                    {t(
                                        "event.liveStreamModal.error.salesEnded"
                                    )}
                                </p>
                            )}
                            <Link
                                className={twMerge(
                                    "btn btn-primary ",
                                    isAdmin ? "flex" : "hidden"
                                )}
                            >
                                Open as Admin
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
